import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Alert,
  Checkbox,
  Typography,
  Progress,
  Empty,
  Spin,
  Tag,
  theme,
  Grid,
} from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ThunderboltFilled,
  InboxOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import { getOrderCascadePreviewApi } from 'api/hallazgoFlujo';
import { useTenantColors } from 'hooks/useTenantColors';

const hexToRgba = (hex, alpha = 0.18) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(148, 163, 184, ${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Fase 6 del roadmap Hallazgos->Presupuesto->ODT.
// Modal que se abre antes de firmar una ODT. Muestra el preview de la cascada
// de cierre: cuales hallazgos YA se cerraron (tareas finish=1 con etapa
// RESUELTO — la cascada se dispara en cada finishOrderItem), cuales quedarán
// EN_ODT pendientes (tareas no finalizadas), y tareas genericas sin hallazgo.
//
// Al confirmar dispara callback onConfirm — el caller se encarga de llamar al
// endpoint de firma (postFinishOrder) con las signatures. Este modal es el
// momento "wow": muestra visualmente el impacto del cierre antes de ejecutarlo.
//
// Animación de entrada escalonada (150ms * index) en los items para que el
// usuario vea aparecer los hallazgos como "resolviendose" uno a uno.

const INYECT_ID = 'cascade-closure-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(INYECT_ID)) {
  const style = document.createElement('style');
  style.id = INYECT_ID;
  // cascadeResolvedPulse usa var(--cascade-pulse-color) — se setea inline
  // en cada elemento segun el token.colorSuccess activo del tenant.
  style.textContent = `
    @keyframes cascadeItemFadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes cascadeResolvedPulse {
      0%, 100% { box-shadow: 0 0 0 0 var(--cascade-pulse-color, rgba(46, 107, 58, 0)); }
      50%      { box-shadow: 0 0 0 4px var(--cascade-pulse-color-mid, rgba(46, 107, 58, 0.18)); }
    }
  `;
  document.head.appendChild(style);
}

function SectionHeader({ title, count, color, icon, tone = 'default' }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color, fontSize: 14 }}>{icon}</span>
        <Typography.Text
          strong
          style={{
            fontSize: 11,
            letterSpacing: 0.8,
            color: '#64748b',
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          {title.toUpperCase()}
        </Typography.Text>
      </div>
      <Tag color={tone} style={{ marginInlineEnd: 0 }}>
        {count}
      </Tag>
    </div>
  );
}

function CascadeClosureModal({
  open,
  idOrder,
  onClose,
  onConfirm,
  confirmLabel = 'Firmar y cerrar ODT',
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const { secondary } = useTenantColors();
  const { token } = theme.useToken();
  // Lo abre el técnico desde el teléfono al firmar la ODT. La etiqueta de
  // confirmación crece con la cuenta de hallazgos ("Firmar y cerrar ODT
  // (3 hallazgos)") y en una fila de 358 px no entra junto a Cancelar.
  const screens = Grid.useBreakpoint();
  const compact = !screens.md;
  const successColor = token.colorSuccess;
  const successTint = useMemo(() => hexToRgba(successColor, 0.15), [successColor]);
  const secondaryTint = useMemo(() => hexToRgba(secondary, 0.15), [secondary]);

  useEffect(() => {
    if (!open || !idOrder) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    setSelected(new Set());
    getOrderCascadePreviewApi(idOrder)
      .then((res) => {
        if (res && typeof res.totalTareas === 'number') {
          setPreview(res);
        } else {
          setError(res?.message || 'No se pudo obtener el preview');
        }
      })
      .catch((err) => setError(err?.message || 'Error al consultar el preview'))
      .finally(() => setLoading(false));
  }, [open, idOrder]);

  // Pre-marca porCerrar (tareas finish=1 con hallazgo en EN_ODT) y huerfanos
  // (hallazgos del budget sin order_item) — el cierre por default cubre todo
  // lo que "obviamente" debe cerrarse. pendientes (tareas finish=0) NO se
  // pre-marcan: el tecnico decide si las da por resueltas. cerrados ya estan
  // RESUELTO, no entran en la seleccion.
  useEffect(() => {
    if (!preview) return;
    const initial = new Set();
    (preview.porCerrar || []).forEach((e) => {
      if (e.hallazgo?.id) initial.add(e.hallazgo.id);
    });
    (preview.huerfanos || []).forEach((e) => {
      if (e.hallazgo?.id) initial.add(e.hallazgo.id);
    });
    setSelected(initial);
  }, [preview]);

  const toggleSelection = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const progressPct = preview
    ? Math.round((preview.tareasFinalizadas / Math.max(1, preview.totalTareas)) * 100)
    : 0;

  const renderItem = (entry, idx, variant, options = {}) => {
    const { checkable = false, keyOverride } = options;
    const bg =
      variant === 'cerrado'
        ? successTint
        : variant === 'pendiente' || variant === 'huerfano'
        ? secondaryTint
        : '#f6f7f9';
    const border =
      variant === 'cerrado'
        ? successColor
        : variant === 'pendiente' || variant === 'huerfano'
        ? secondary
        : '#cbd2d9';
    const hallazgoId = entry.hallazgo?.id;
    const checked = checkable && hallazgoId ? selected.has(hallazgoId) : false;
    const rowKey = keyOverride ?? entry.idItem ?? `h-${hallazgoId}`;
    return (
      <div
        key={rowKey}
        style={{
          padding: 10,
          background: bg,
          borderLeft: `3px solid ${border}`,
          borderRadius: 6,
          animation: `cascadeItemFadeIn 0.24s cubic-bezier(0.22,0.8,0.32,1) both ${idx * 80}ms${
            variant === 'cerrado' ? ', cascadeResolvedPulse 2.8s ease-in-out infinite' : ''
          }`,
          ...(variant === 'cerrado'
            ? {
                '--cascade-pulse-color': hexToRgba(successColor, 0),
                '--cascade-pulse-color-mid': hexToRgba(successColor, 0.18),
              }
            : {}),
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        {checkable && hallazgoId ? (
          <Checkbox
            checked={checked}
            onChange={() => toggleSelection(hallazgoId)}
            style={{ marginTop: 2 }}
          />
        ) : (
          <span
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: '#64748b',
              paddingTop: 2,
              minWidth: 28,
            }}
          >
            {entry.idItem ? `#${entry.idItem}` : ''}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {entry.hallazgo ? (
              <HallazgoChip
                hallazgo={{
                  id: entry.hallazgo.id,
                  tipo: entry.hallazgo.tipo,
                  severidad: entry.hallazgo.severidad,
                  descripcion: entry.hallazgo.descripcion,
                }}
                compact
              />
            ) : null}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#475569',
              marginTop: 4,
            }}
          >
            {variant === 'huerfano' ? (
              <span style={{ color: '#94a3b8' }}>
                Hallazgo del presupuesto sin tarea asociada en la ODT
              </span>
            ) : (
              <>
                <span style={{ color: '#94a3b8' }}>Tarea:</span>{' '}
                {entry.itemDescription || entry.description} · {entry.elevator}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ThunderboltFilled style={{ color: secondary, fontSize: 18 }} />
          <span>Cierre de ODT {idOrder ? `#${idOrder}` : ''}</span>
        </div>
      }
      onCancel={onClose}
      width={620}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: compact ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <Button key="cancel" onClick={onClose} block={compact}>
            Cancelar
          </Button>
          <Button
            key="confirm"
            type="primary"
            icon={<CheckCircleFilled />}
            disabled={!preview || loading}
            block={compact}
            onClick={() => onConfirm?.({ preview, hallazgosACerrar: Array.from(selected) })}
            style={{ background: secondary, borderColor: secondary }}
          >
            {selected.size > 0
              ? `${confirmLabel} (${selected.size} hallazgo${selected.size === 1 ? '' : 's'})`
              : confirmLabel}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin /> <span style={{ marginLeft: 10 }}>Calculando cascada…</span>
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : preview ? (
        <>
          {/* Progreso global */}
          <div
            style={{
              padding: 12,
              background: token.colorTextBase,
              color: '#fff',
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 8,
              }}
            >
              <Typography.Text
                style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, letterSpacing: 0.8 }}
              >
                PROGRESO DE TAREAS
              </Typography.Text>
              <span
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#fff',
                }}
              >
                {preview.tareasFinalizadas} / {preview.totalTareas}
              </span>
            </div>
            <Progress
              percent={progressPct}
              strokeColor={secondary}
              trailColor="rgba(255,255,255,0.12)"
              showInfo={false}
              size="small"
            />
          </div>

          {/* Seccion: hallazgos ya resueltos (informativo, sin checkbox) */}
          {preview.cerrados.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                title="Hallazgos ya resueltos"
                count={preview.cerrados.length}
                color={successColor}
                tone="success"
                icon={<CheckCircleFilled />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {preview.cerrados.map((e, idx) => renderItem(e, idx, 'cerrado'))}
              </div>
            </div>
          ) : null}

          {/* Seccion: tareas finalizadas cuyo hallazgo sigue abierto (pre-marcadas) */}
          {preview.porCerrar && preview.porCerrar.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                title="Tareas finalizadas · cerrar hallazgo"
                count={preview.porCerrar.length}
                color={successColor}
                tone="success"
                icon={<CheckCircleFilled />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {preview.porCerrar.map((e, idx) =>
                  renderItem(e, idx, 'pendiente', { checkable: true })
                )}
              </div>
            </div>
          ) : null}

          {/* Seccion: hallazgos del presupuesto sin tarea (huerfanos, pre-marcados) */}
          {preview.huerfanos && preview.huerfanos.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                title="Hallazgos del presupuesto sin tarea"
                count={preview.huerfanos.length}
                color={secondary}
                tone="processing"
                icon={<LinkOutlined />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {preview.huerfanos.map((e, idx) =>
                  renderItem(e, idx, 'huerfano', {
                    checkable: true,
                    keyOverride: `huerfano-${e.hallazgo.id}`,
                  })
                )}
              </div>
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 10, fontSize: 12 }}
                message="Estos hallazgos estaban vinculados al presupuesto pero no quedaron como tareas en la ODT. Marcalos si los resolviste durante el trabajo."
              />
            </div>
          ) : null}

          {/* Seccion: tareas no finalizadas (no pre-marcadas; el tecnico decide) */}
          {preview.pendientes.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                title="Tareas pendientes (no finalizadas)"
                count={preview.pendientes.length}
                color={secondary}
                tone="warning"
                icon={<ClockCircleOutlined />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {preview.pendientes.map((e, idx) =>
                  renderItem(e, idx, 'pendiente', { checkable: true })
                )}
              </div>
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 10, fontSize: 12 }}
                message="Por default quedan EN_ODT. Marca solo los que realmente se resolvieron."
              />
            </div>
          ) : null}

          {/* Seccion: sin hallazgo (informativo, no aplica checkbox) */}
          {preview.sinHallazgo.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader
                title="Tareas genericas"
                count={preview.sinHallazgo.length}
                color="#64748b"
                icon={<InboxOutlined />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {preview.sinHallazgo.map((e, idx) => renderItem(e, idx, 'generica'))}
              </div>
            </div>
          ) : null}

          {preview.totalTareas === 0 ? (
            <Empty description="La ODT no tiene tareas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

export default CascadeClosureModal;
